import React, { useEffect, useState } from 'react';

// =========================================================================
// Inline SVG icons
// =========================================================================

const CheckIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

const DocumentDollarIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M12 12v6" />
    <path d="M14.25 13.5h-3a1.25 1.25 0 100 2.5h1.5a1.25 1.25 0 110 2.5h-3" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 17 9 11 13 15 21 7" />
    <polyline points="14 7 21 7 21 14" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 11h18" />
  </svg>
);

const CheckCircleSolidIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1.1 14.6l-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4-7 7z" />
  </svg>
);

const InfoIcon = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const DownChevron = () => (
  <div className="flex justify-center text-slate-300" aria-hidden="true">
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="M5 12l7 7 7-7" />
    </svg>
  </div>
);

// Down chevron + a small "+N newly past-due" note for inflow on the
// Customer Success Story (light mode counterpart of LoginPage's InflowMarker).
const InflowMarker = ({ count }) => (
  <div className="flex flex-col items-center gap-0.5" aria-hidden="true">
    <svg className="h-5 w-5 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="M5 12l7 7 7-7" />
    </svg>
    <span className="text-[10px] md:text-[11px] text-slate-500 leading-none">
      +{count} newly past-due
    </span>
  </div>
);

const UpArrowIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </svg>
);

const WarningTriangleIcon = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// =========================================================================
// Count-up animation hook + AnimatedNumber component
// =========================================================================

const useCountUp = (target, durationMs = 1100) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(target)) {
      setValue(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

// Animates a string like "$142,350" or "0.1%" by parsing prefix/number/suffix.
const AnimatedNumber = ({ value }) => {
  const match = String(value).match(/^([^\d.-]*)([\d,.-]+)(.*)$/);
  const numericStr = match ? match[2].replace(/,/g, '') : '0';
  const targetNum = parseFloat(numericStr);
  // Call the hook unconditionally so render order stays stable.
  const current = useCountUp(targetNum);
  if (!match) return <>{value}</>;
  const prefix = match[1];
  const suffix = match[3];
  const decimals = numericStr.includes('.') ? numericStr.split('.')[1].length : 0;
  const display = current.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return <>{prefix}{display}{suffix}</>;
};

// =========================================================================
// Tooltip
// =========================================================================

const InfoTooltip = ({ children }) => (
  <span className="group relative inline-flex items-center text-slate-400 hover:text-slate-600 cursor-help">
    <InfoIcon />
    <span
      className="pointer-events-none absolute left-5 top-0 z-20 w-64 origin-top-left scale-95 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-150 rounded-md bg-slate-900 text-white text-[11px] leading-relaxed font-normal normal-case tracking-normal p-2.5 shadow-lg"
      role="tooltip"
    >
      {children}
    </span>
  </span>
);

// =========================================================================
// Sparkline + Delta pill
// =========================================================================

const Sparkline = ({ data, width = 76, height = 24, color = '#10b981' }) => {
  if (!data || data.length < 2) return null;
  const pad = 1.5;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = (width - pad * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = pad + i * step;
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y];
  });
  const line = `M ${points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' L ')}`;
  const lastX = points[points.length - 1][0];
  const area = `${line} L ${lastX.toFixed(2)},${height} L ${pad},${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={area} fill={color} fillOpacity="0.14" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const DeltaPill = ({ children }) => (
  <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
    <UpArrowIcon />
    {children}
  </span>
);

// =========================================================================
// Freshness badge — tells the owner whether staff is keeping BSI imports current.
// =========================================================================

const FRESHNESS_STYLES = {
  fresh: {
    label: 'Fresh',
    badgeBg: 'bg-emerald-50',
    badgeBorder: 'border-emerald-200',
    text: 'text-emerald-700',
    metaText: 'text-emerald-700/80',
    icon: <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />,
  },
  attention: {
    label: 'Attention',
    badgeBg: 'bg-amber-50',
    badgeBorder: 'border-amber-200',
    text: 'text-amber-700',
    metaText: 'text-amber-700/80',
    icon: <WarningTriangleIcon />,
  },
  stale: {
    label: 'Stale',
    badgeBg: 'bg-red-50',
    badgeBorder: 'border-red-200',
    text: 'text-red-700',
    metaText: 'text-red-700/80',
    icon: <AlertCircleIcon />,
  },
};

const FreshnessBadge = ({ status, daysAgo }) => {
  const styles = FRESHNESS_STYLES[status];
  if (!styles) return null;
  const dayLabel = daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
  return (
    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
      <div
        className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 ${styles.badgeBg} ${styles.badgeBorder}`}
      >
        <span className={`flex items-center ${styles.text}`}>{styles.icon}</span>
        <span className={`text-[11px] font-semibold ${styles.text}`}>{styles.label}</span>
      </div>
      <span className={`text-[11px] ${styles.metaText}`}>Updated {dayLabel}</span>
      <InfoTooltip>
        This status reflects how recently BSI data was imported for this category.
      </InfoTooltip>
    </div>
  );
};

// =========================================================================
// Reusable cards
// =========================================================================

const MetricCard = ({
  icon,
  title,
  tooltip,
  thirtyDay,
  ninetyDay,
  thirtyDaySubtext,
  ninetyDaySubtext,
  delta,
  trend,
  freshness,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 md:p-6 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600">
          {icon}
        </div>
        <h3 className="text-sm font-semibold text-slate-700 inline-flex items-center gap-1.5">
          {title}
          {tooltip ? <InfoTooltip>{tooltip}</InfoTooltip> : null}
        </h3>
      </div>
      {trend ? <Sparkline data={trend} /> : null}
    </div>
    <div className="mt-5 grid grid-cols-2 gap-4">
      <div className="border-r border-slate-100 pr-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">30 Days</div>
        <div className="mt-1 text-3xl font-bold text-slate-900 tabular-nums">
          <AnimatedNumber value={thirtyDay} />
        </div>
        {thirtyDaySubtext ? (
          <div className="mt-1 text-[11px] text-slate-500">{thirtyDaySubtext}</div>
        ) : null}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold">90 Days</div>
        <div className="mt-1 text-3xl font-bold text-emerald-600 tabular-nums">
          <AnimatedNumber value={ninetyDay} />
        </div>
        {ninetyDaySubtext ? (
          <div className="mt-1 text-[11px] text-slate-500">{ninetyDaySubtext}</div>
        ) : null}
        {delta ? <DeltaPill>{delta}</DeltaPill> : null}
      </div>
    </div>
    {freshness ? <FreshnessBadge status={freshness.status} daysAgo={freshness.daysAgo} /> : null}
  </div>
);

const SummaryMetric = ({ value, label, large }) => (
  <div>
    <div
      className={`font-bold tracking-tight tabular-nums leading-none ${
        large ? 'text-5xl md:text-6xl' : 'text-2xl md:text-3xl'
      }`}
    >
      <AnimatedNumber value={value} />
    </div>
    <div className={`mt-1 ${large ? 'text-sm' : 'text-xs md:text-sm'} text-emerald-50/90`}>
      {label}
    </div>
  </div>
);

const RoiStat = ({ label, value, subtext, emphasize, tooltip }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium inline-flex items-center gap-1.5">
      {label}
      {tooltip ? <InfoTooltip>{tooltip}</InfoTooltip> : null}
    </div>
    <div
      className={`mt-1 font-bold tabular-nums ${
        emphasize ? 'text-3xl md:text-4xl text-emerald-600' : 'text-2xl md:text-3xl text-slate-900'
      }`}
    >
      <AnimatedNumber value={value} />
    </div>
    {subtext ? (
      <div className="mt-1 text-[11px] text-slate-500 font-medium">{subtext}</div>
    ) : null}
  </div>
);

const ScoreCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
    <div className="mt-2 text-3xl font-bold text-slate-900 tabular-nums">
      <AnimatedNumber value={value} />
    </div>
  </div>
);

const BacklogCard = ({ backlog }) => {
  const cleared = backlog <= 0;
  return (
    <div
      className={`rounded-xl border shadow-sm p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
        cleared ? 'bg-emerald-50 border-emerald-300' : 'bg-orange-50 border-orange-200'
      }`}
    >
      <div
        className={`text-[11px] uppercase tracking-wider font-semibold ${
          cleared ? 'text-emerald-700' : 'text-orange-700'
        }`}
      >
        Current Backlog Remaining
      </div>
      {cleared ? (
        <div className="mt-2 flex items-center gap-2 text-emerald-700">
          <CheckCircleSolidIcon />
          <span className="text-xl font-bold">Backlog Cleared</span>
        </div>
      ) : (
        <div className="mt-2 text-3xl font-bold text-orange-600 tabular-nums">
          <AnimatedNumber value={String(backlog)} />
        </div>
      )}
    </div>
  );
};

const StepCard = ({ count, label, subLabel, highlight }) => (
  <div
    className={`rounded-xl px-4 py-3 md:px-5 md:py-4 transition-all duration-200 ${
      highlight
        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md hover:shadow-lg'
        : 'bg-white border border-slate-200 text-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5'
    }`}
  >
    <div className="flex items-center gap-3 md:gap-4">
      {count !== undefined && count !== null ? (
        <span
          className={`text-xl md:text-2xl font-bold tabular-nums leading-none ${
            highlight ? 'text-white' : 'text-slate-900'
          }`}
        >
          {count}
        </span>
      ) : null}
      <span
        className={`text-sm md:text-base ${
          highlight ? 'font-semibold' : 'text-slate-600'
        }`}
      >
        {label}
      </span>
      {highlight ? (
        <span className="ml-auto text-white/90">
          <CheckCircleSolidIcon />
        </span>
      ) : null}
    </div>
    {subLabel ? (
      <div
        className={`mt-1 text-xs md:text-sm font-semibold ${
          highlight ? 'text-emerald-50' : 'text-emerald-700'
        }`}
      >
        {subLabel}
      </div>
    ) : null}
  </div>
);

// =========================================================================
// Page
// =========================================================================

const BusinessImpact = () => {
  // In production these come from the API. Hardcoded for the demo.
  const currentBacklog = 18;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* ─── 90-Day Business Results (green executive panel) — first thing visitors see ── */}
      <div
        className="rounded-2xl p-6 md:p-8 text-white shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/15">
            <TrendingUpIcon />
          </span>
          <h2 className="text-lg md:text-xl font-semibold">90-Day Business Results</h2>
        </div>
        <p className="mt-1 text-sm text-emerald-50/85 max-w-2xl">
          Based on invoices, quotes, and tests that are no longer active in imported BSI reports.
        </p>

        {/* Hero metric + supporting grid */}
        <div className="mt-6 flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-emerald-100/80 font-semibold">
              Revenue Recovered
            </div>
            <div className="mt-1 text-5xl md:text-6xl font-bold tracking-tight tabular-nums leading-none">
              <AnimatedNumber value="$58,300" />
            </div>
            <div className="mt-2 text-xs text-emerald-50/80 max-w-xs">
              Estimated cash on resolved invoices and scheduled tests.
            </div>
          </div>

          <div className="md:border-l md:border-white/15 md:pl-10 grid grid-cols-2 md:grid-cols-4 gap-5 flex-1">
            <SummaryMetric value="73" label="Invoices Resolved" />
            <SummaryMetric value="18" label="Quotes Resolved" />
            <SummaryMetric value="$142,350" label="Quote Value Advanced" />
            <SummaryMetric value="119" label="Tests Scheduled" />
          </div>
        </div>

        <p className="mt-6 text-sm text-emerald-50/90">
          Helping your staff stay on top of past due invoices, open quotes, and upcoming tests.
        </p>
      </div>

      {/* ─── Header ───────────────────────────────────────────────────── */}
      <header>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Business Impact</h1>
        <p className="mt-1 text-sm md:text-base text-slate-500">
          Track the revenue and business results generated through consistent follow-up.
        </p>
      </header>

      {/* ─── 2x2 metric cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <MetricCard
          icon={<CheckIcon />}
          title="Past Due Invoices Resolved"
          tooltip="Invoices that were past due in a prior BSI report but no longer appear in the latest upload. Estimated collected dollars use an average invoice value."
          thirtyDay="18"
          ninetyDay="73"
          thirtyDaySubtext="Estimated Collected: $11,900"
          ninetyDaySubtext="Estimated Collected: $48,200"
          delta="+18 resolved this month"
          trend={[3, 4, 5, 4, 6, 5, 7, 6, 7, 8, 9, 7, 9]}
          freshness={{ status: 'attention', daysAgo: 9 }}
        />
        <MetricCard
          icon={<DocumentDollarIcon />}
          title="Quotes Resolved"
          tooltip="Open quotes that have left the BSI open-quotes export since the last upload (won, lost, or withdrawn)."
          thirtyDay="4"
          ninetyDay="18"
          delta="+4 resolved this month"
          trend={[0, 1, 1, 2, 1, 2, 1, 2, 2, 1, 2, 3, 2]}
          freshness={{ status: 'stale', daysAgo: 21 }}
        />
        <MetricCard
          icon={<TrendingUpIcon />}
          title="Quote Value Advanced"
          tooltip="Dollar value of quotes that have moved out of the open-quotes pipeline. Pipeline advancement is not yet collected revenue."
          thirtyDay="$32,500"
          ninetyDay="$142,350"
          delta="+$32,500 this month"
          trend={[8000, 9000, 11000, 9500, 10500, 12000, 10000, 13000, 11500, 14000, 13000, 15500, 17000]}
          freshness={{ status: 'stale', daysAgo: 21 }}
        />
        <MetricCard
          icon={<CalendarIcon />}
          title="Tests Scheduled"
          tooltip="Upcoming tests moved into a scheduled state since the last BSI report. Estimated revenue uses an average test value."
          thirtyDay="47"
          ninetyDay="119"
          thirtyDaySubtext="Estimated Revenue: $4,000"
          ninetyDaySubtext="Estimated Revenue: $10,100"
          delta="+47 scheduled this month"
          trend={[6, 7, 9, 8, 10, 9, 11, 10, 12, 9, 11, 13, 12]}
          freshness={{ status: 'stale', daysAgo: 18 }}
        />
      </div>

      {/* ─── ROI Panel (conservative framing) ─────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 transition-shadow hover:shadow-md">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Estimated Return on Investment
          </h2>
          <InfoTooltip>
            A conservative view: compares subscription cost to the value of work cycled through follow-up. Pipeline advanced ($142,350) is shown separately from recovered cash ($58,300) — pipeline is not yet collected revenue.
          </InfoTooltip>
        </div>
        <p className="text-sm text-slate-500">Cost versus value worked, last 90 days.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-5">
          <RoiStat
            label="Monthly Subscription"
            value="$149"
            subtext="+ $799 setup (one-time)"
            tooltip="Your BackflowOffice subscription is $149 per month after a one-time $799 implementation fee (data import, tenant branding, onboarding call, priority support)."
          />
          <RoiStat
            label="90-Day Revenue Recovered"
            value="$58,300"
            tooltip="Estimated cash collected on resolved past-due invoices plus estimated revenue on scheduled tests."
          />
          <RoiStat
            label="90-Day Pipeline Advanced"
            value="$142,350"
            tooltip="Dollar value of quotes that left the open-quotes pipeline. Not yet booked as collected revenue."
          />
          <RoiStat
            label="Cost vs. Value Worked"
            value="0.6%"
            emphasize
            tooltip="90-day total cost ($799 setup + 3 × $149 = $1,246) as a percentage of total value cycled through follow-up ($58,300 recovered + $142,350 advanced = $200,650)."
          />
        </div>

        <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-start gap-3">
          <span className="mt-0.5 text-emerald-600">
            <CheckCircleSolidIcon />
          </span>
          <p className="text-sm text-emerald-800">
            One recovered commercial quote can often pay for years of service.
          </p>
        </div>
      </div>

      {/* ─── Follow-Up Performance scoreboard ─────────────────────────── */}
      <section>
        <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">
          Follow-Up Performance
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <ScoreCard label="Invoices Resolved This Month" value="18" />
          <ScoreCard label="Quotes Resolved This Month" value="4" />
          <ScoreCard label="Tests Scheduled This Month" value="47" />
          <BacklogCard backlog={currentBacklog} />
        </div>
      </section>

      {/* ─── Customer Success Story (merged with backlog timeline) ────── */}
      <section>
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">Customer Success Story</h2>
          <span className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wider">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Actual Customer Results
          </span>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Started with <span className="font-semibold text-slate-700">103 past due invoices</span>.
          {' '}
          While <span className="font-semibold text-slate-700">~40 new invoices</span> became past-due,
          {' '}
          <span className="font-semibold text-emerald-700">73 were paid</span> and
          {' '}
          <span className="font-semibold text-emerald-700">$48,200 collected</span>. Backlog now caught up.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl">
          {/* Left: Follow-Up Activity (decreasing) */}
          <div>
            <h3 className="text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500 leading-tight">
              Follow-Up Activity
            </h3>
            <p className="text-center text-[10px] text-slate-400 mb-3 leading-tight">
              Past 30+ Days Due
            </p>
            <div className="space-y-2">
              <StepCard count="103" label="Past Due Invoices" />
              <InflowMarker count="8" />
              <StepCard count="72" label="Left to Contact" />
              <InflowMarker count="12" />
              <StepCard count="41" label="Left to Contact" />
              <InflowMarker count="9" />
              <StepCard count="18" label="Left to Contact" />
              <InflowMarker count="11" />
              <StepCard count="~15" label="Active · Caught Up" highlight />
            </div>
          </div>

          {/* Right: Overdue Invoices Paid (increasing) */}
          <div>
            <h3 className="text-center text-[11px] uppercase tracking-wider font-semibold text-emerald-700 leading-tight">
              Overdue Invoices Paid
            </h3>
            <p className="text-center text-[10px] text-emerald-700/60 mb-3 leading-tight">
              Cumulative · 90 Days
            </p>
            <div className="space-y-2">
              <StepCard count="0" label="Paid" />
              <DownChevron />
              <StepCard count="10" label="Paid" />
              <DownChevron />
              <StepCard count="30" label="Paid" />
              <DownChevron />
              <StepCard count="57" label="Paid" />
              <DownChevron />
              <StepCard count="73" label="Paid" subLabel="$48,200 Collected" highlight />
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs md:text-sm italic text-slate-500 max-w-2xl mx-auto">
          Past-due isn&rsquo;t a problem you solve once — it&rsquo;s a tide you stay ahead of.
        </p>
      </section>
    </div>
  );
};

export default BusinessImpact;
